import { useMutation, useQuery } from '@apollo/client';
import {
  AcceptOffersIcon,
  AlertService,
  Button,
  EmptyStateIcon,
  InformationIconSmall,
  Search,
  Theme,
  Timing,
  usePolling,
} from '@spoiler-alert/ui-library';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Link } from 'react-router-dom';
import deleteCachedFieldsOnListedInventoriesQuery from '../../apollo/cache-helpers/delete-cached-fields-on-listed-inventories-query';
import deleteCachedFieldsOnUserQuery from '../../apollo/cache-helpers/delete-cached-fields-on-user-query';
import RecommendedOfferShifts from '../../components/banners/recommended-offer-shifts';
import UpdatedAwardedOffers from '../../components/banners/updated-awarded-offers';
import { getColumnsFromDataTableProfile } from '../../components/data-table';
import PostAwardUpdatesModal from '../../components/modals/post-award-updates-modal';
import { featureFlags } from '../../enums';
import { AcceptShiftedOfferListings, acceptOffer } from '../../graphql/mutations';
import { AwardSummaryQuery, OfferReviewPollingQuery, SuggestedOfferShiftsByBuyerQuery } from '../../graphql/queries';
import checkFeature from '../../helpers/check-feature-flag';
import routePaths from '../../route-paths';
import { TitleService } from '../../services';
import { Breadcrumbs, UserFilters } from '../../store';
import SendLossEmailsModal from '../offers/send-loss-emails-modal';
import StaticLoading from '../static-loading';
import OfferReviewBulkContext from './offer-review-bulk-action-context';
import OfferReviewBuyerCard from './offer-review-buyer-card';

const useStyles = createUseStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    borderBottom: `1px ${Theme.borderColor} solid`,
    padding: '16px 0px',
    '&:first-child': {
      padding: '0px 0px 16px 0px',
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0px 0px 24px',
    '& button': {
      marginLeft: 8,
    },
    position: 'relative',
  },
  offerReviewSearch: {
    width: 500,
    marginRight: 'auto',
  },
  buttonIcon: {
    '& svg': {
      width: '24px !important',
      height: '24px !important',
    },
  },
  loader: {
    margin: 'calc(50vh - 118px) auto',
    textAlign: 'center',
  },
  emptyState: {
    padding: '24px 0px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  emptyStateHeader: {
    fontSize: 16,
    fontWeight: 500,
    marginTop: 10,
  },
  emptyStateText: {
    fontSize: 16,
  },
  awardOffersModalContainer: {
    display: 'flex',
    background: '#FDF7F7',
    padding: '8px 16px',
    margin: '16px 0px',
  },
  outerContainer: {
    padding: '20px',
  },
  saveSettings: {
    display: 'flex',
    justifyContent: 'flex-end',
    boxShadow: 'inset 0px 1px 0 0 #d9dee1',
    padding: '24px',
    width: '100%',
    '& button': {
      marginLeft: 8,
    },
  },
  infoIcon: {
    display: 'flex',
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
    fill: Theme.teal,
  },
  laneActivated: {
    padding: 12,
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    alignSelf: 'stretch',
    backgroundColor: Theme.teal5,
    marginBottom: 24,
    borderRadius: 4,
  },
  laneActivatedBody: {
    fontSize: 14,
    height: 19,
  },
});

const exportStatusStringMap = {
  IN_PROGRESS: 'ACCEPTANCE IN PROGRESS',
  COMPLETE: 'ACCEPTED',
  NOT_IN_PROGRESS: 'NOT ACCEPTED',
  STARTED: 'PENDING',
};

const UI_LOCK_STATUS = {
  OPEN: 'OPEN',
  LOCKED: 'LOCKED',
};

const offerCompFilterState = {
  sites: [],
  status: [
    { text: 'Offers Made', value: 'Offers Made' },
    { text: 'Offers Awarded', value: 'Offers Awarded' },
    { text: 'Partially Awarded', value: 'Partially Awarded' },
  ],
  showTied: false,
  belowReservePrice: false,
  suggestedAward: false,
  postAwardUpdates: true,
  categories: [],
  businessUnits: [],
  truckTypes: [],
  negotiationStatus: [],
  logistics: [],
  inventoryTypes: [],
  codeDate: { start: undefined, end: undefined },
  trucklaneStatus: [],
  distributionLists: [],
  importBatches: [],
};

const OfferReviewV2 = ({ user, history }) => {
  const classes = useStyles();
  const [searchText, setSearchText] = useState('');
  const [showSendLossEmailsModal, setShowSendLossEmailsModal] = useState(false);
  const [displayAwardedOffersModal, setDisplayAwardedOffersModal] = useState(false);
  const [showActivatedLaneBanner, setShowActivatedLaneBanner] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const { data: suggestedOfferShiftResults, loading: suggestedOfferShiftLoading } = useQuery(SuggestedOfferShiftsByBuyerQuery, {
    fetchPolicy: 'network-only',
  });
  const { data: awardSummaryResults, loading: awardSummaryLoading, refetch: refetchAwardSummary } = useQuery(AwardSummaryQuery);
  const [acceptOfferMutation, { loading: accepting }] = useMutation(acceptOffer);
  const [acceptShifts, { loading: acceptingShifts }] = useMutation(AcceptShiftedOfferListings);
  const postAwardModalRef = useRef(false);

  const statusPolling = usePolling({
    query: OfferReviewPollingQuery,
    poll: true,
    responseKey: 'offerReviewPollingQuery',
    idleInterval: 10000,
    activeInterval: 1000,
    isInProgress: () => accepting,
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    TitleService.setTitles('Offer Review');
    Breadcrumbs.set([
      {
        url: routePaths.review,
        title: 'Offer Review',
      },
    ]);
  }, []);

  useEffect(() => {
    if (postAwardModalRef.current) {
      postAwardModalRef.current = false;
      UserFilters.updateFilters('offerComparison', offerCompFilterState);
      history.push(`${routePaths.offerComparison}`);
    }
  }, [displayAwardedOffersModal]);

  const buyerPollingMap = useMemo(() => {
    const statusMap = new Map();
    if (statusPolling) {
      statusPolling?.exportStatuses.forEach((e) => {
        statusMap.set(e.buyerSiteName, e.exportStatus);
      });
    }
    return statusMap;
  }, [statusPolling]);

  const displayPostUpdatesAward = useMemo(() => {
    let displayPostUpdatesAwardFlag = false;
    if (statusPolling && statusPolling?.displayPostAwardUpdates) {
      displayPostUpdatesAwardFlag = true;
    }
    return displayPostUpdatesAwardFlag;
  }, [statusPolling]);

  const numberOfPostAwardUpdatedOffers = awardSummaryResults?.currentUserQuery.getAwardSummary.results.reduce((acc, cur) => {
    return acc + cur.totalPostAwardUpdates;
  }, 0);

  const noOffersToAccept = () =>
    (!statusPolling || statusPolling?.uiLockStatus === UI_LOCK_STATUS.OPEN) &&
    !awardSummaryResults?.currentUserQuery.getAwardSummary.results?.filter((result) => {
      return exportStatusStringMap[result.exportStatus] === exportStatusStringMap.NOT_IN_PROGRESS;
    }).length;

  const handleSearch = Timing.debounce((text) => setSearchText(text), 300);

  const handleAcceptAll = Timing.throttle(async () => {
    let buyerList = [];
    if (awardSummaryResults)
      buyerList = awardSummaryResults?.currentUserQuery.getAwardSummary.results.map((buyer) => ({
        ...buyer,
        _id: `${buyer.transactionLocationName}-${buyer.siteName}`,
      }));
    const buyerSiteIds = buyerList.filter((buyer) => buyerPollingMap.get(buyer.siteName) === 'NOT_IN_PROGRESS').map((b) => b.siteId);
    const { data } = await acceptOfferMutation({
      variables: { buyerSiteIds, acceptAll: true },
      update: (cache) => {
        deleteCachedFieldsOnUserQuery(cache, ['awardedOfferListings', 'awardedInventoryFilterParameters', 'getAwardSummary']);
        deleteCachedFieldsOnListedInventoriesQuery(cache);
      },
    });

    if (data.acceptOffer.errors.length) {
      AlertService.alert({
        type: 'warning',
        message: (
          <span>
            {'Sorry there was an error accepting all offers. If this problem persists, please contact a Spoiler Alert Administrator to help you.'}
          </span>
        ),
      });
      refetchAwardSummary();
    }
  }, 1000);

  const cols = useMemo(() => {
    const ignoredFields = ['transactionLocationName', 'logisticsTerm', 'siteName'];
    const dtpCols = [...getColumnsFromDataTableProfile('Offer Comp Buyer Flyup and Offer Review', user.site.dataTableProfiles)];
    return dtpCols
      .map((col) => {
        if (ignoredFields.includes(col.field)) return null;
        return col;
      })
      .filter((empties) => !!empties);
  }, []);

  const shiftSuggestions = useMemo(() => {
    if (!checkFeature(featureFlags.offerShift)) return new Map();
    if (suggestedOfferShiftResults?.suggestedOfferShiftsByBuyerQuery?.errors?.length)
      AlertService.alert({
        type: 'warning',
        message:
          'There was an unknown error while trying to load opportunities. Refresh this page, and if the issue persists, contact support. Offers can still be accepted.',
      });
    const shiftSuggestionMap = new Map();
    if (suggestedOfferShiftResults?.suggestedOfferShiftsByBuyerQuery?.buyers?.length) {
      suggestedOfferShiftResults?.suggestedOfferShiftsByBuyerQuery.buyers.forEach((buyer) => {
        shiftSuggestionMap.set(buyer._id, buyer);
      });
    }
    return shiftSuggestionMap;
  }, [suggestedOfferShiftResults]);

  const groupBuyerData = (currentBuyer, newBuyer) => {
    currentBuyer.acceptedOffer += newBuyer.acceptedOffer;
    currentBuyer.casesAwarded += newBuyer.casesAwarded;
    currentBuyer.totalPostAwardUpdates += newBuyer.totalPostAwardUpdates;
    currentBuyer.totalCost += newBuyer.totalCost;
    currentBuyer.cubeAdjustedWeightAwarded += newBuyer.cubeAdjustedWeightAwarded;
    currentBuyer.donatedCost += newBuyer.donatedCost;
    currentBuyer.netObsolescenceImpact += newBuyer.netObsolescenceImpact;
    currentBuyer.originalQuantity += newBuyer.originalQuantity;
    currentBuyer.palletsAwarded += newBuyer.palletsAwarded;
    currentBuyer.priceOfInventoryAwarded += newBuyer.priceOfInventoryAwarded;
    currentBuyer.poundsAwarded += newBuyer.poundsAwarded;
    currentBuyer.totalCases += newBuyer.totalCases;
    currentBuyer.totalPrice += newBuyer.totalPrice;
    currentBuyer.totalWeight += newBuyer.totalWeight;
    currentBuyer.writeOffPrice += newBuyer.writeOffPrice;
    currentBuyer.writeOffPriceOfInventoryAwarded += newBuyer.writeOffPriceOfInventoryAwarded;
    currentBuyer.unitPriceRecoveryRate += newBuyer.unitPriceRecoveryRate;
    currentBuyer.costRecoveryRate = (currentBuyer.acceptedOffer / currentBuyer.totalCost) * 100;
    currentBuyer.discountPercent = (1 - currentBuyer.acceptedOffer / currentBuyer.totalPrice) * 100;
    currentBuyer.writeOffDiscountPercent = 1 - currentBuyer.acceptedOffer / currentBuyer.writeOffPriceOfInventoryAwarded;
  };

  const sortedBuyerOffers = useMemo(() => {
    const buyerMap = new Map();
    if (awardSummaryResults?.currentUserQuery.getAwardSummary.results.length) {
      JSON.parse(JSON.stringify(awardSummaryResults?.currentUserQuery.getAwardSummary.results)).forEach((result) => {
        if (buyerMap.has(result.siteName)) return groupBuyerData(buyerMap.get(result.siteName), result);
        return buyerMap.set(result.siteName, result);
      });
      return [...buyerMap.values()].sort((a, b) => {
        const nameA = a.siteName.toUpperCase();
        const nameB = b.siteName.toUpperCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
    }
    return [];
  }, [awardSummaryResults]);

  const zeroState = () => {
    return (
      <div className={classes.emptyState}>
        <EmptyStateIcon />
        <div className={classes.emptyStateHeader}>No offers have been awarded yet.</div>
        <div className={classes.emptyStateText}>When you award offers, they will show up here.</div>
      </div>
    );
  };

  const buyerCard = useMemo(() => {
    if (!awardSummaryLoading && !suggestedOfferShiftLoading) {
      const buyerCards = [];
      const searchRegex = new RegExp(searchText.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'), 'i');
      sortedBuyerOffers.forEach((buyerData) => {
        const matchesSearch = searchText.trim() !== '' ? searchRegex.test([buyerData.siteName, buyerData.transactionLocationName]) : true;
        if (matchesSearch)
          buyerCards.push(
            <div key={buyerData.siteName} className={classes.card}>
              <OfferReviewBuyerCard
                user={user}
                buyerData={buyerData}
                status={buyerPollingMap.get(buyerData.siteName)}
                history={history}
                columns={cols}
                shiftSuggestions={shiftSuggestions.get(buyerData.siteId)}
                hasShiftSuggestions={shiftSuggestions.has(buyerData.siteId)}
                setShowActivatedLaneBanner={setShowActivatedLaneBanner}
              />
            </div>,
          );
      });
      return buyerCards.length ? buyerCards : zeroState();
    }
    return <StaticLoading classes={{ container: classes.loader }} />;
  }, [awardSummaryLoading, suggestedOfferShiftLoading, sortedBuyerOffers, shiftSuggestions, buyerPollingMap, searchText]);

  const displayShiftBanner = () => {
    if (!checkFeature(featureFlags.offerShift)) return false;
    let display = false;
    sortedBuyerOffers.forEach((buyerData) => {
      if (buyerData.exportStatus === 'NOT_IN_PROGRESS') display = true;
    });
    return display;
  };

  const displayAwardedOfferModalHandler = () => {
    setDisplayAwardedOffersModal(true);
  };

  const onSendLossEmailsSubmit = async (storefrontName, dropDate) => {
    const { data } = await sendLossEmails({
      variables: { storefrontName, dropDate },
    });

    if (data.sendLossEmails.errors.length) {
      AlertService.alert({
        type: 'warning',
        message: (
          <span>
            {'Sorry there was an error sending loss emails. If this problem persists, please contact a Spoiler Alert Administrator to help you.'}
          </span>
        ),
      });
    } else {
      AlertService.alert({
        type: 'success',
        message: <span>{'Loss emails sent successfully'}</span>,
      });
    }
  };

  const navigateToOfferCompScreen = () => {
    postAwardModalRef.current = true;
    setDisplayAwardedOffersModal(false);
  };

  const closeAwardedOffersModal = () => {
    setDisplayAwardedOffersModal(false);
  };

  const { totalOpportunity, hasShiftsToAccept } = useMemo(() => {
    let totalOpportunity = 0;
    let hasShiftsToAccept = false;
    shiftSuggestions.forEach((buyerData) => {
      buyerData.trucklanes.forEach((lane) => {
        lane.originalOfferListings.forEach((offer) => {
          offer.offerShifts.forEach((shift) => {
            if (shift.status === 'PROPOSED') totalOpportunity += shift.shiftedQuantity * shift.unitPrice;
          });
        });
      });
    });
    if (totalOpportunity > 0) hasShiftsToAccept = true;
    return { totalOpportunity, hasShiftsToAccept };
  }, [shiftSuggestions]);

  const awardBulkShifts = Timing.throttle(async () => {
    setBulkLoading(true);
    const shiftAcceptance = [];
    sortedBuyerOffers.forEach((buyerData) => {
      if (shiftSuggestions.has(buyerData.siteId)) {
        const shifts = { buyerSiteName: buyerData.siteName, shiftedOfferListings: [] };
        shiftSuggestions.get(buyerData.siteId).trucklanes.forEach((lane) => {
          lane.originalOfferListings.forEach((offer) => {
            offer.offerShifts.forEach((shift) => {
              if (shift.status !== 'PROPOSED') return;
              shifts.shiftedOfferListings.push({
                shiftedInventoryId: shift.shiftInventoryId,
                pricePerCase: shift.unitPrice,
                shiftedQuantity: shift.shiftedQuantity,
                logisticsOption: shift.logisticsOptions,
                originalOfferId: offer._id,
                id: shift._id,
                shiftType: shift.shiftType,
              });
            });
          });
        });
        shiftAcceptance.push(shifts);
      }
    });

    try {
      const results = await acceptShifts({
        variables: {
          offerShifts: shiftAcceptance,
        },
        refetchQueries: [{ query: AwardSummaryQuery }],
      });

      if (results.data.acceptShiftedOfferListings.errors.length) throw new Error(results.data.acceptShiftedOfferListings.errors[0].message);
      AlertService.alert({
        type: 'success',
        message: <span>{`${results.data.acceptShiftedOfferListings.offerShifts.length} offer shifts have been awarded.`}</span>,
        autoDismiss: true,
      });
    } catch (error) {
      AlertService.alert({
        type: 'warning',
        message: (
          <span>
            {error.message
              ? error.message
              : 'Sorry there was an error accepting offer shifts. If this problem persists, please contact a Spoiler Alert Administrator to help you.'}
          </span>
        ),
        autoDismiss: true,
      });
    }
    setBulkLoading(false);
  }, 1000);

  const acceptAllDisabled =
    !(statusPolling?.uiLockStatus !== UI_LOCK_STATUS.LOCKED) ||
    accepting ||
    acceptingShifts ||
    awardSummaryLoading ||
    suggestedOfferShiftLoading ||
    noOffersToAccept();
  const acceptAllShiftsDisabled = accepting || acceptingShifts || !hasShiftsToAccept || bulkLoading;

  const modalInnerText = `${numberOfPostAwardUpdatedOffers || 0} of your customer's have offers that have changed since they were awarded. Offers cannot be accepted for
  these customers until the updated offers are approved or rejected.`;

  const activatedLaneBanner = () => {
    return (
      <div className={classes.laneActivated}>
        <InformationIconSmall className={classes.infoIcon} />
        <div className={classes.laneActivatedBody}>
          Some truck lanes have gone from inactive to active. We recommend visiting <Link to={routePaths.offerComparison}>Offer Comparison</Link>{' '}
          before accepting to ensure all available inventory for these trucks is added.
        </div>
      </div>
    );
  };

  return (
    <div>
      {displayShiftBanner() && (
        <RecommendedOfferShifts
          user={user}
          history={history}
          opportunityAmount={totalOpportunity}
          awardBulkShifts={awardBulkShifts}
          loading={acceptingShifts}
          suggestedOfferShiftLoading={suggestedOfferShiftLoading}
          acceptAllShiftsDisabled={acceptAllShiftsDisabled}
        />
      )}
      <div className={classes.actionBar}>
        <Search onChange={handleSearch} className={classes.offerReviewSearch} />
        {checkFeature(featureFlags.sendLossEmails) && (
          <Button secondary onClick={() => setShowSendLossEmailsModal(true)}>
            Send Loss Emails
          </Button>
        )}
        <Button
          classes={{ iconHolder: classes.buttonIcon }}
          primary
          disabled={acceptAllDisabled}
          loading={accepting}
          onClick={!displayPostUpdatesAward ? handleAcceptAll : displayAwardedOfferModalHandler}
          icon={AcceptOffersIcon}
          loadingText="Accepting All Offers"
          cypressTag="accept-all-offers-button"
        >
          Accept All Offers
        </Button>
      </div>
      {showActivatedLaneBanner && activatedLaneBanner()}
      {displayPostUpdatesAward && <UpdatedAwardedOffers numberOfOffers={numberOfPostAwardUpdatedOffers} onClick={displayAwardedOfferModalHandler} />}
      <OfferReviewBulkContext.Provider
        value={{
          bulkLoading,
          acceptShifts,
        }}
      >
        {buyerCard}
      </OfferReviewBulkContext.Provider>
      <div id="showScroll" />
      <SendLossEmailsModal
        onHide={() => setShowSendLossEmailsModal(false)}
        user={user}
        open={showSendLossEmailsModal}
        onSubmit={onSendLossEmailsSubmit}
      />
      <PostAwardUpdatesModal
        displayAwardedOffersModal={displayAwardedOffersModal}
        navigateToOfferCompScreen={navigateToOfferCompScreen}
        cancel={closeAwardedOffersModal}
        modalInnerText={modalInnerText}
      />
    </div>
  );
};

OfferReviewV2.propTypes = {
  user: PropTypes.object,
  history: PropTypes.object,
};

export default OfferReviewV2;
